"use client";

import { c as _c } from "react/compiler-runtime";
import type { Media } from "@spree/sdk";
import { ZoomIn } from "lucide-react";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { useCallback, useRef, useState } from "react";
import { ProductImage } from "@/components/ui/product-image";
const SWIPE_THRESHOLD_PX = 50;
const SWIPE_MAX_VERTICAL_PX = 75;

/** Tiny 10×10 neutral gray PNG used as a blur placeholder while images load. */
const BLUR_PLACEHOLDER = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAIElEQVQYV2P4////MwwMDAxMDAwMDGQJMJCvkGwNZCsEAGebBwVss9lRAAAAAElFTkSuQmCC";

/** Lazy-loaded lightbox — only pulled into the bundle when a user zooms. */
const LazyMediaLightbox = dynamic(() => import("@/components/products/MediaLightbox").then(mod => ({
  default: mod.MediaLightbox
})), {
  ssr: false,
  // Minimal fullscreen overlay so the zoom click gives immediate
  // feedback on slow networks while the chunk downloads.
  loading: () => <div className="fixed inset-0 z-50 bg-black/90" aria-hidden="true" />
});
interface MediaGalleryProps {
  images: Media[];
  productName: string;
  activeIndex?: number | null;
}

/** Prefer pre-sized Spree media URLs over the full-resolution original,
 * so the Next.js image optimizer doesn't have to fetch the source file. */
function getMainImageUrl(media: Media | undefined): string | null {
  if (!media) return null;
  return media.xlarge_url || media.large_url || media.original_url || null;
}
function getThumbImageUrl(media: Media | undefined): string | null {
  if (!media) return null;
  return media.small_url || media.mini_url || media.original_url || null;
}
export function MediaGallery(props) {
  const $ = _c(3);
  const t0 = props.activeIndex ?? "default";
  let t1;
  if ($[0] !== props || $[1] !== t0) {
    t1 = <MediaGalleryInner key={t0} {...props} />;
    $[0] = props;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function MediaGalleryInner(t0) {
  const $ = _c(44);
  const {
    images,
    productName,
    activeIndex
  } = t0;
  const t = useTranslations("products");
  const [selectedIndex, setSelectedIndex] = useState(activeIndex ?? 0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [mainImageErrorUrl, setMainImageErrorUrl] = useState(null);
  const safeIndex = Math.max(0, Math.min(selectedIndex, images.length - 1));
  const touchStartRef = useRef(null);
  const suppressClickRef = useRef(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = e => {
      const touch = e.touches[0];
      if (!touch) {
        return;
      }
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY
      };
      suppressClickRef.current = false;
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleTouchStart = t1;
  let t2;
  if ($[1] !== images.length || $[2] !== safeIndex) {
    t2 = e_0 => {
      const start = touchStartRef.current;
      touchStartRef.current = null;
      if (!start || images.length <= 1) {
        return;
      }
      const touch_0 = e_0.changedTouches[0];
      if (!touch_0) {
        return;
      }
      const dx = touch_0.clientX - start.x;
      const dy = touch_0.clientY - start.y;
      if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dy) > SWIPE_MAX_VERTICAL_PX) {
        return;
      }
      suppressClickRef.current = true;
      const nextIndex = dx < 0 ? (safeIndex + 1) % images.length : (safeIndex - 1 + images.length) % images.length;
      setSelectedIndex(nextIndex);
      setMainImageErrorUrl(null);
    };
    $[1] = images.length;
    $[2] = safeIndex;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleTouchEnd = t2;
  if (images.length === 0) {
    let t3;
    if ($[4] !== productName) {
      t3 = <div className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden"><ProductImage src={null} alt={productName} fill={true} iconClassName="w-24 h-24" /></div>;
      $[4] = productName;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = index => {
      setSelectedIndex(index);
      setMainImageErrorUrl(null);
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const selectImage = t3;
  const selectedImage = images[safeIndex];
  let t4;
  if ($[7] !== selectedImage) {
    t4 = getMainImageUrl(selectedImage);
    $[7] = selectedImage;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const mainImageUrl = t4;
  const showMainImage = mainImageUrl && mainImageErrorUrl !== mainImageUrl;
  let t5;
  if ($[9] !== showMainImage) {
    t5 = () => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        return;
      }
      if (showMainImage) {
        setIsZoomed(true);
      }
    };
    $[9] = showMainImage;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("openImageZoom");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = !showMainImage;
  const t8 = selectedImage?.alt || productName;
  let t9;
  if ($[13] !== mainImageUrl) {
    t9 = () => mainImageUrl && setMainImageErrorUrl(mainImageUrl);
    $[13] = mainImageUrl;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== mainImageUrl || $[16] !== safeIndex || $[17] !== t8 || $[18] !== t9) {
    t10 = <ProductImage key={safeIndex} src={mainImageUrl} alt={t8} fill={true} className="object-cover" fetchPriority="high" loading="eager" priority={true} quality={85} sizes="(max-width: 768px) 100vw, 50vw" placeholder="blur" blurDataURL={BLUR_PLACEHOLDER} iconClassName="w-24 h-24" onError={t9} />;
    $[15] = mainImageUrl;
    $[16] = safeIndex;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== showMainImage || $[21] !== t) {
    t11 = showMainImage && <div className="absolute bottom-4 right-4 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-lg text-sm text-gray-600 flex items-center gap-1.5"><ZoomIn className="w-4 h-4" />{t("clickToZoom")}</div>;
    $[20] = showMainImage;
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== handleTouchEnd || $[24] !== t10 || $[25] !== t11 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7) {
    t12 = <button type="button" className="relative aspect-square bg-gray-100 rounded-xl overflow-hidden cursor-zoom-in w-full touch-pan-y" onClick={t5} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} aria-label={t6} disabled={t7}>{t10}{t11}</button>;
    $[23] = handleTouchEnd;
    $[24] = t10;
    $[25] = t11;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== images || $[31] !== productName || $[32] !== safeIndex) {
    t13 = images.length > 1 && <div className="flex gap-2 overflow-x-auto pb-2">{images.map((image, index_0) => {
        const thumbUrl = getThumbImageUrl(image);
        return <button type="button" key={image.id} onClick={() => selectImage(index_0)} className={`relative w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden border-2 transition-colors bg-gray-100 ${index_0 === safeIndex ? "border-gray-600" : "border-transparent hover:border-gray-300"}`}><ProductImage src={thumbUrl} alt={image.alt || `${productName} ${index_0 + 1}`} fill={true} className="object-cover" sizes="80px" /></button>;
      })}</div>;
    $[30] = images;
    $[31] = productName;
    $[32] = safeIndex;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== images || $[35] !== isZoomed || $[36] !== productName || $[37] !== safeIndex || $[38] !== showMainImage) {
    t14 = isZoomed && showMainImage && <LazyMediaLightbox images={images} activeIndex={safeIndex} productName={productName} onClose={() => setIsZoomed(false)} onNavigate={selectImage} />;
    $[34] = images;
    $[35] = isZoomed;
    $[36] = productName;
    $[37] = safeIndex;
    $[38] = showMainImage;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t12 || $[41] !== t13 || $[42] !== t14) {
    t15 = <div className="flex flex-col gap-4">{t12}{t13}{t14}</div>;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  return t15;
}